import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ListTodo } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { TaskForm } from "../components/tasks/TaskForm";
import { TaskCard } from "../components/tasks/TaskCard";

const tabCls = (active) =>
  `px-4 h-[38px] text-xs font-semibold uppercase tracking-wider transition-colors ${
    active ? "bg-[color:var(--primary)] text-white" : "bg-white hover:bg-[color:var(--bg)]"
  }`;

export default function Tasks() {
  const { user } = useAuth();
  const elevated = ["admin", "supervisor"].includes(user?.role);
  const isSupervisor = user?.role === "supervisor";
  const [scope, setScope] = useState(elevated ? "all" : "mine");
  const [section, setSection] = useState("open");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/tasks", { params: { scope } })
      .then(({ data }) => setTasks(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [scope]);

  const doneCount = tasks.filter((t) => t.status === "tamamlandi").length;
  const visible = tasks.filter((t) => (section === "done") === (t.status === "tamamlandi"));

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (task, s) => {
    if (s === "tamamlandi" && !window.confirm(`"${task.title}" tamamlandı olarak işaretlenecek ve kilitlenecek. Emin misiniz?`)) return;
    if (task.status === "tamamlandi" && !window.confirm(`"${task.title}" görevi yeniden açılsın mı?`)) return;
    try {
      await api.patch(`/tasks/${task.id}/status`, { status: s });
      toast.success("Görev durumu güncellendi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (task) => {
    if (!window.confirm(`"${task.title}" görevi silinsin mi?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Görev silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={`grid gap-4 ${elevated ? "lg:grid-cols-[1fr_360px]" : ""}`}>
      <div className="space-y-4 min-w-0">
        <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex flex-wrap items-center gap-3">
          <ListTodo size={18} />
          <div className="ticker text-[color:var(--text-3)]">/// GÖREVLER</div>
          {elevated && (
            <div className="flex hard border-2 border-[color:var(--border)] overflow-hidden">
              <button data-testid="tasks-scope-all" onClick={() => setScope("all")} className={tabCls(scope === "all")}>Tüm Görevler</button>
              <button data-testid="tasks-scope-mine" onClick={() => setScope("mine")} className={tabCls(scope === "mine")}>Bana Atananlar</button>
            </div>
          )}
          <div className="flex hard border-2 border-[color:var(--border)] overflow-hidden">
            <button data-testid="tasks-section-open" onClick={() => setSection("open")} className={tabCls(section === "open")}>
              Açık Görevler · {tasks.length - doneCount}
            </button>
            <button data-testid="tasks-section-done" onClick={() => setSection("done")} className={tabCls(section === "done")}>
              Tamamlananlar · {doneCount}
            </button>
          </div>
          <div className="ticker text-[color:var(--text-3)] ml-auto" data-testid="tasks-count">
            {loading ? "Yükleniyor…" : `${visible.length} görev`}
          </div>
        </div>

        {!loading && visible.length === 0 && (
          <div data-testid="tasks-empty" className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-12 text-center text-[color:var(--text-3)]">
            Görev bulunmuyor.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          {visible.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              canChange={elevated || t.assigned_to === user?.id}
              canDelete={t.status === "tamamlandi" ? isSupervisor : elevated}
              canReopen={isSupervisor}
              onStatus={changeStatus}
              onDelete={remove}
            />
          ))}
        </div>
      </div>
      {elevated && <TaskForm onCreated={load} />}
    </div>
  );
}
